<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps({
  percentage: {
    type: Number,
    required: true
  },
  color: {
    type: String,
    default: 'green'
  },
  height: {
    type: Number,
    default: 4
  },
  text: {
    type: String,
    default: ''
  },
  subtext: {
    type: String,
    default: ''
  }
})

const colorMap: Record<string, string> = {
  green: '#15803d',  // green-700
  blue: '#2563eb',   // blue-600
  red: '#b91c1c',    // red-700
  yellow: '#a16207', // yellow-700
  orange: '#c2410c', // orange-700
  purple: '#7e22ce', // purple-700
  metal: '#5a6578',  // metal-600
  copper: '#525f6b', // dark silver
  indigo: '#3730a3',   // indigo-800
}

const bgColor = computed(() => colorMap[props.color] || colorMap.green)
</script>

<template>
  <!-- Outer container -->
  <div :class="`w-full rounded relative flex items-center justify-center px-2 text-metal-300 progress-container ${subtext ? 'group cursor-pointer' : ''}`" :style="{ minHeight: Math.max(height, 20) + 'px' }">
    <!-- Inner progress bar -->
    <div
      class="absolute left-0 top-px bottom-px rounded progress-bar"
      :style="{
        width: Math.min(percentage, 100) + '%',
        backgroundColor: bgColor,
        color: bgColor
      }"
    ></div>
    
    <!-- Text container on top of progress bar -->
    <div v-if="text || subtext" class="flex justify-between items-center w-full text-xs relative z-10">
      <!-- Main text - visible by default, hidden on hover only if subtext exists -->
      <span v-if="text" :class="subtext ? 'group-hover:hidden' : ''">{{ text }}</span>
      <!-- Subtext - hidden by default, visible on hover -->
      <span v-if="subtext" class="hidden group-hover:block">{{ subtext }}</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.progress-container {
  background: rgba(35, 42, 55, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow:
    inset 0 2px 6px rgba(0, 0, 0, 0.5),
    0 1px 2px rgba(0, 0, 0, 0.2);
}

.progress-bar {
  background-image: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.15) 0%,
    rgba(255, 255, 255, 0.05) 50%,
    rgba(0, 0, 0, 0.1) 100%
  );
  background-blend-mode: overlay;
  box-shadow:
    0 0 8px currentColor,
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  transition: width 0.3s ease-out;
  margin-left: 0.1rem;
  margin-right: 0.1rem;
}
</style>
